import React from "react";
import ServiceRubricaLote from "@/services/ServiceRubricaLote";
import { buscarTodasPaginas } from "@/lib/paginacao";
import { mensagemErro } from "@/utils/apiError";
import type {
  ProcessamentoLoteItem,
  ProcessamentoLoteStatus,
} from "@/types/rubricas/analise-lote/ProcessamentoLote";

export const INTERVALO_PREVIA_MS = 2000;
export const TENTATIVAS_PREVIA = 60;

const STATUS_EM_PREPARACAO: ProcessamentoLoteStatus[] = [
  "PENDENTE_PUBLICACAO",
  "ENFILEIRADO",
  "EM_PROCESSAMENTO",
];

export type EstadoPrevia =
  | { fase: "ociosa" }
  | { fase: "preparando" }
  | { fase: "pronta"; itens: ReadonlyMap<number, ProcessamentoLoteItem> }
  | { fase: "demorada" }
  | { fase: "falha"; mensagem: string };

/**
 * Acompanha a prévia de uma ACAO_EM_MASSA: o worker valida cada rubrica e deixa o lote em
 * AGUARDANDO_CONFIRMACAO; aí os itens trazem o resultado (APTO/INAPTO/ERRO) por rubrica.
 */
export function usePreviaProcessamento(idProcessamentoLote: string | null) {
  const [estado, setEstado] = React.useState<EstadoPrevia>({ fase: "ociosa" });
  const [tentativa, setTentativa] = React.useState(0);

  React.useEffect(() => {
    if (!idProcessamentoLote) {
      setEstado({ fase: "ociosa" });
      return;
    }
    let cancelado = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setEstado({ fase: "preparando" });

    const consultar = async (consulta: number) => {
      try {
        const { data } = await ServiceRubricaLote.consultarProcessamento(idProcessamentoLote);
        if (cancelado) return;
        if (data.confirmadoEm) {
          setEstado({ fase: "falha", mensagem: "Estas alterações já foram confirmadas." });
        } else if (data.status === "AGUARDANDO_CONFIRMACAO") {
          const itens = await buscarTodasPaginas((page, size) =>
            ServiceRubricaLote.consultarItensProcessamento(idProcessamentoLote, { page, size }),
          );
          if (!cancelado) {
            setEstado({
              fase: "pronta",
              itens: new Map(itens.map((item) => [item.idAnaliseRubrica, item])),
            });
          }
        } else if (STATUS_EM_PREPARACAO.includes(data.status)) {
          if (consulta + 1 >= TENTATIVAS_PREVIA) setEstado({ fase: "demorada" });
          else timer = setTimeout(() => consultar(consulta + 1), INTERVALO_PREVIA_MS);
        } else {
          setEstado({
            fase: "falha",
            mensagem: data.erro?.detalhe || "Não foi possível preparar a prévia das alterações.",
          });
        }
      } catch (err) {
        if (!cancelado) {
          setEstado({
            fase: "falha",
            mensagem: mensagemErro(err, "Não foi possível consultar a prévia das alterações."),
          });
        }
      }
    };

    consultar(0);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [idProcessamentoLote, tentativa]);

  const recarregar = React.useCallback(() => setTentativa((atual) => atual + 1), []);
  return { estado, recarregar };
}
